/**
 * Estado de la conversación del asistente.
 *
 * El **servidor** es la fuente de verdad del historial: aquí sólo se guarda el
 * hilo abierto en pantalla y el id de la conversación, que se persiste en el
 * navegador para que recargar la página continúe donde ibas. Antes el hilo vivía
 * sólo en `localStorage`, así que se perdía al cambiar de equipo; ahora cada
 * conversación queda en `asistente_conversaciones` y se reabre desde el
 * historial.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ChatMessage, ChatRole, ConversacionDetalle } from './asistente.types';

interface AsistenteState {
  messages: ChatMessage[];
  loading: boolean;
  /** Conversación abierta (null = todavía no se ha preguntado nada). */
  conversacionId: number | null;
  /** Título de la conversación abierta. */
  conversacionTitulo: string | null;
  addMessage: (message: Omit<ChatMessage, 'id' | 'createdAt'>) => ChatMessage;
  setLoading: (loading: boolean) => void;
  /** Recuerda la conversación que devolvió el backend al responder. */
  setConversacion: (id: number | null, titulo?: string | null) => void;
  /** Carga en pantalla una conversación guardada del historial. */
  abrirConversacion: (conversacion: ConversacionDetalle) => void;
  /** Guarda la valoración de una respuesta ya enviada. */
  setFeedback: (messageId: string, util: boolean) => void;
  reset: () => void;
}

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Convierte los turnos guardados en mensajes de la pantalla.
 *
 * El detalle guardado trae gráficos, tablas, enlaces y acciones tal como se
 * enviaron, así que el hilo reabierto se dibuja igual que el original.
 */
export function turnosAMensajes(conversacion: ConversacionDetalle): ChatMessage[] {
  return conversacion.turnos.map((turno) => ({
    id: `guardado-${turno.id}`,
    role: turno.rol,
    content: turno.contenido,
    charts: turno.detalle?.charts ?? [],
    cards: turno.detalle?.cards ?? [],
    tablas: turno.detalle?.tablas ?? [],
    enlaces: turno.detalle?.enlaces ?? [],
    acciones: turno.detalle?.acciones ?? [],
    tools: turno.detalle?.tools_used ?? [],
    consultaId: turno.detalle?.consulta_id ?? null,
    createdAt: Date.parse(turno.creado_en.replace(' ', 'T')) || Date.now(),
  }));
}

export const useAsistenteStore = create<AsistenteState>()(
  persist(
    (set) => ({
      messages: [],
      loading: false,
      conversacionId: null,
      conversacionTitulo: null,

      addMessage: (message) => {
        const complete: ChatMessage = { ...message, id: createId(), createdAt: Date.now() };
        set((state) => ({ messages: [...state.messages, complete] }));
        return complete;
      },

      setLoading: (loading) => set({ loading }),

      setConversacion: (id, titulo) =>
        set((state) => ({
          conversacionId: id,
          conversacionTitulo: titulo === undefined ? state.conversacionTitulo : titulo,
        })),

      abrirConversacion: (conversacion) =>
        set({
          messages: turnosAMensajes(conversacion),
          conversacionId: conversacion.id,
          conversacionTitulo: conversacion.titulo,
          loading: false,
        }),

      setFeedback: (messageId, util) =>
        set((state) => ({
          messages: state.messages.map((message) =>
            message.id === messageId ? { ...message, feedback: util } : message,
          ),
        })),

      reset: () => set({ messages: [], conversacionId: null, conversacionTitulo: null, loading: false }),
    }),
    {
      name: 'edsuite-asistente',
      // Sólo se recuerda qué conversación estaba abierta: el contenido se lee del
      // servidor, así no se muestran cifras antiguas guardadas en el navegador.
      version: 1,
      partialize: (state) => ({ conversacionId: state.conversacionId }),
      // La versión anterior guardaba los mensajes en el navegador; se descartan
      // (el historial vive en el servidor desde esta versión).
      migrate: (persisted) => ({
        conversacionId: (persisted as { conversacionId?: number | null } | undefined)?.conversacionId ?? null,
      }),
    },
  ),
);

/** Últimos mensajes para enviar como contexto (el backend también los limita). */
export function selectHistory(messages: ChatMessage[], limit = 12): { role: 'user' | 'assistant'; content: string }[] {
  return messages
    .filter((message): message is ChatMessage & { role: 'user' | 'assistant' } => message.role !== 'error')
    .slice(-limit)
    .map((message) => ({ role: message.role, content: message.content }));
}

export const selectMessages = (state: AsistenteState): ChatMessage[] => state.messages;
export const selectLoading = (state: AsistenteState): boolean => state.loading;
export const selectConversacionId = (state: AsistenteState): number | null => state.conversacionId;

/** Etiqueta legible del rol (para el avatar/accesibilidad). */
export const ROLE_LABELS: Record<ChatRole, string> = {
  user: 'Tú',
  assistant: 'Asistente IA',
  error: 'Error',
};
